import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdChevronLeft, MdChevronRight, MdClose } from 'react-icons/md';

interface Lead {
    _id: string;
    sellerId: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
    } | null;
    buyerId: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
    } | null;
    orderNumber: string;
    status: string;
    approvalStatus?: 'pending' | 'approved' | 'rejected' | 'conflict';
    leadrsRate: number;
    customerRate: number;
    comment?: string;
    leadPrice?: number;
    buyerPrice?: number;
    createdAt: string;
    body: Array<{ field: string; answer: string }>;
}

export default function BackOfficeLeads() {
    const [leads, setLeads] = useState<Lead[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [statusFilter, setStatusFilter] = useState('all');
    const [approvalFilter, setApprovalFilter] = useState('all');
    const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, statusFilter, approvalFilter]);

    const fetchLeads = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const params: any = { page, limit: 50 };
            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }
            if (approvalFilter !== 'all') {
                params.approvalStatus = approvalFilter;
            }

            const response = await axios.get(`${CONFIG.apiUrl}/admin/leads`, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });
            
            setLeads(response.data.leads);
            setTotal(response.data.total);
            setTotalPages(response.data.pages);
        } catch (error) {
            console.error('Error fetching leads:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'validated':
            case 'paid':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'pending':
            case 'waiting':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            case 'rejected':
            case 'refused':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const getApprovalStatusColor = (status?: string) => {
        switch (status?.toLowerCase()) {
            case 'approved':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'pending':
                return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
            case 'conflict':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 font-bold';
            case 'rejected':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const handleApprovalStatusChange = async (leadId: string, newStatus: 'approved' | 'rejected') => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${CONFIG.apiUrl}/lead/${leadId}/approval-status`,
                { approvalStatus: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // Refresh leads after update
            fetchLeads();
        } catch (error) {
            console.error('Error updating approval status:', error);
            alert('Failed to update approval status');
        }
    };

    const handleAdminReject = async (leadId: string) => {
        const reason = window.prompt('Rejection reason:');
        if (!reason || reason.trim().length === 0) return;

        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${CONFIG.apiUrl}/lead/${leadId}/admin-reject`,
                { rejectionReason: reason },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            fetchLeads();
        } catch (error) {
            console.error('Error rejecting lead:', error);
            alert('Failed to reject lead');
        }
    };

    const handleViewLead = (lead: Lead) => {
        setSelectedLead(lead);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedLead(null);
    };

    return (
        <div className="pt-[50px]">
            <Card extra="p-5">
                <div className="mb-5 flex items-center justify-between gap-2">
                    <div>
                        <div className="text-2xl font-bold text-navy-700 dark:text-white">
                            Leads Management
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            {total.toLocaleString()} total leads
                        </div>
                    </div>
                </div>

                <div className="mb-5 flex gap-2">
                    <select
                        value={statusFilter}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                            setStatusFilter(e.target.value);
                            setPage(1);
                        }}
                        className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                        style={{ maxWidth: '200px' }}
                    >
                        <option value="all">All Status</option>
                        <option value="validated">Validated</option>
                        <option value="pending">Pending</option>
                        <option value="rejected">Rejected</option>
                        <option value="paid">Paid</option>
                    </select>
                    
                    <select
                        value={approvalFilter}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                            setApprovalFilter(e.target.value);
                            setPage(1);
                        }}
                        className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                        style={{ maxWidth: '200px' }}
                    >
                        <option value="all">All Approvals</option>
                        <option value="pending">Pending</option>
                        <option value="conflict">🚨 Conflicts</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                    </select>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-navy-600">
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Order #
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Transaction
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Status
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Approval
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Leadrs Rate
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Customer Rate
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Comment
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Seller
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Buyer
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Commission
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Date
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={12} className="py-4 text-center">
                                        Loading...
                                    </td>
                                </tr>
                            ) : leads.length === 0 ? (
                                <tr>
                                    <td colSpan={12} className="py-4 text-center">
                                        No leads found
                                    </td>
                                </tr>
                            ) : (
                                leads.map((lead) => (
                                    <tr
                                        key={lead._id}
                                        className="border-b border-gray-100 dark:border-navy-700"
                                    >
                                        <td className="py-3 text-sm font-mono">
                                            {lead.orderNumber || lead._id.slice(-8)}
                                        </td>
                                        <td className="py-3 text-sm">
                                            <div className="flex flex-col gap-2">
                                                {/* Buyer */}
                                                <div className="flex flex-col">
                                                    {lead.buyerId ? (
                                                        <>
                                                            <div className="font-medium text-blue-600 dark:text-blue-400">
                                                                {lead.buyerId.firstName} {lead.buyerId.lastName}
                                                            </div>
                                                            <div className="text-xs text-gray-500">
                                                                {lead.buyerId.email}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <span className="text-gray-500">-</span>
                                                    )}
                                                </div>
                                                {/* Arrow */}
                                                <div className="flex items-center text-gray-400">
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                                    </svg>
                                                </div>
                                                {/* Seller */}
                                                <div className="flex flex-col">
                                                    {lead.sellerId ? (
                                                        <>
                                                            <div className="font-medium text-purple-600 dark:text-purple-400">
                                                                {lead.sellerId.firstName} {lead.sellerId.lastName}
                                                            </div>
                                                            <div className="text-xs text-gray-500">
                                                                {lead.sellerId.email}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <span className="text-gray-500">-</span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-3">
                                            <span
                                                className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusColor(
                                                    lead.status
                                                )}`}
                                            >
                                                {lead.status}
                                            </span>
                                        </td>
                                        <td className="py-3">
                                            <div className="flex flex-col gap-2">
                                                {lead.approvalStatus ? (
                                                    <span
                                                        className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getApprovalStatusColor(
                                                            lead.approvalStatus
                                                        )}`}
                                                    >
                                                        {lead.approvalStatus === 'conflict' && '🚨 '}
                                                        {lead.approvalStatus}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-500">-</span>
                                                )}
                                                {lead.approvalStatus === 'conflict' && (
                                                    <div className="flex gap-1">
                                                        <button
                                                            onClick={() => handleApprovalStatusChange(lead._id, 'approved')}
                                                            className="rounded bg-green-500 px-2 py-1 text-xs text-white hover:bg-green-600"
                                                            title="Approve lead"
                                                        >
                                                            Envoyer à l'acheteur
                                                        </button>
                                                        <button
                                                            onClick={() => handleApprovalStatusChange(lead._id, 'rejected')}
                                                            className="rounded bg-red-500 px-2 py-1 text-xs text-white hover:bg-red-600"
                                                            title="Reject lead"
                                                        >
                                                            Retour vendeur
                                                            
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-3 text-sm">
                                            {lead.leadrsRate && `${lead.leadrsRate}/10`}
                                        </td>
                                        <td className="py-3 text-sm font-medium">
                                            {lead.customerRate >= 0 ? `${lead.customerRate}/10` : '-'}
                                        </td>
                                        <td className="py-3 text-sm text-gray-600 dark:text-gray-400">
                                            {lead.comment || '-'}
                                        </td>
                                        <td className="py-3 text-sm font-medium">
                                            {lead.leadPrice ? `€${lead.leadPrice.toFixed(2)}` : '-'}
                                        </td>
                                        <td className="py-3 text-sm font-medium">
                                            {lead.buyerPrice ? `€${lead.buyerPrice.toFixed(2)}` : '-'}
                                        </td>
                                        <td className="py-3 text-sm font-medium text-green-600 dark:text-green-400">
                                            {lead.buyerPrice && lead.leadPrice ? `€${(lead.buyerPrice - lead.leadPrice).toFixed(2)}` : '-'}
                                        </td>
                                        <td className="py-3 text-sm">
                                            {new Date(lead.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="py-3">
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleViewLead(lead)}
                                                    className="rounded bg-blue-500 px-2 py-1 text-xs text-white hover:bg-blue-600"
                                                    title="View lead details"
                                                >
                                                    View
                                                </button>
                                                {lead.status !== 'rejected' && lead.approvalStatus !== 'rejected' && (
                                                    <button
                                                        onClick={() => handleAdminReject(lead._id)}
                                                        className="rounded bg-red-500 px-2 py-1 text-xs text-white hover:bg-red-600"
                                                        title="Admin reject this lead"
                                                    >
                                                        Reject
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="mt-5 flex items-center justify-between">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Page {page} of {totalPages}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage(page - 1)}
                                disabled={page === 1}
                                className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                            >
                                <MdChevronLeft />
                                Previous
                            </button>
                            <button
                                onClick={() => setPage(page + 1)}
                                disabled={page === totalPages}
                                className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                            >
                                Next
                                <MdChevronRight />
                            </button>
                        </div>
                    </div>
                )}
            </Card>

            {/* Lead Details Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    {/* Backdrop */}
                    <div 
                        className="absolute inset-0 bg-black bg-opacity-50" 
                        onClick={closeModal}
                    />
                    
                    {/* Modal Content */}
                    <div className="relative z-10 w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white dark:bg-navy-800 rounded-lg shadow-xl m-4">
                        {/* Header */}
                        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 dark:border-navy-600 bg-white dark:bg-navy-800 px-6 py-4">
                            <h2 className="text-xl font-bold text-navy-700 dark:text-white">Lead Details</h2>
                            <button
                                onClick={closeModal}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                                <MdClose size={24} />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="px-6 py-4">
                            {selectedLead && (
                                <div className="space-y-6">
                                    <div>
                                        <h3 className="text-lg font-semibold mb-3 text-navy-700 dark:text-white">Lead Information</h3>
                                        <div className="grid grid-cols-2 gap-3 text-sm">
                                            <div>
                                                <span className="font-medium text-gray-700 dark:text-gray-300">Order Number:</span>
                                                <span className="ml-2 text-gray-900 dark:text-white">{selectedLead.orderNumber || selectedLead._id.slice(-8)}</span>
                                            </div>
                                            <div>
                                                <span className="font-medium text-gray-700 dark:text-gray-300">Status:</span>
                                                <span className="ml-2 text-gray-900 dark:text-white">{selectedLead.status}</span>
                                            </div>
                                            <div>
                                                <span className="font-medium text-gray-700 dark:text-gray-300">Created:</span>
                                                <span className="ml-2 text-gray-900 dark:text-white">{new Date(selectedLead.createdAt).toLocaleString()}</span>
                                            </div>
                                            {selectedLead.approvalStatus && (
                                                <div>
                                                    <span className="font-medium text-gray-700 dark:text-gray-300">Approval:</span>
                                                    <span className="ml-2 text-gray-900 dark:text-white">{selectedLead.approvalStatus}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-semibold mb-3 text-navy-700 dark:text-white">Buyer</h3>
                                        {selectedLead.buyerId ? (
                                            <div className="text-sm">
                                                <div className="text-gray-900 dark:text-white">{selectedLead.buyerId.firstName} {selectedLead.buyerId.lastName}</div>
                                                <div className="text-gray-600 dark:text-gray-400">{selectedLead.buyerId.email}</div>
                                            </div>
                                        ) : (
                                            <div className="text-sm text-gray-500 dark:text-gray-400">No buyer information</div>
                                        )}
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-semibold mb-3 text-navy-700 dark:text-white">Seller</h3>
                                        {selectedLead.sellerId ? (
                                            <div className="text-sm">
                                                <div className="text-gray-900 dark:text-white">{selectedLead.sellerId.firstName} {selectedLead.sellerId.lastName}</div>
                                                <div className="text-gray-600 dark:text-gray-400">{selectedLead.sellerId.email}</div>
                                            </div>
                                        ) : (
                                            <div className="text-sm text-gray-500 dark:text-gray-400">No seller information</div>
                                        )}
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-semibold mb-3 text-navy-700 dark:text-white">Lead Body</h3>
                                        {selectedLead.body && selectedLead.body.length > 0 ? (
                                            <div className="space-y-3">
                                                {selectedLead.body.map((item, index) => (
                                                    <div key={index} className="border-b border-gray-200 dark:border-navy-600 pb-2">
                                                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.field}</div>
                                                        <div className="text-sm text-gray-900 dark:text-white mt-1">{item.answer || '-'}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-sm text-gray-500 dark:text-gray-400">No lead data available</div>
                                        )}
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-semibold mb-3 text-navy-700 dark:text-white">Pricing & Ratings</h3>
                                        <div className="grid grid-cols-2 gap-3 text-sm">
                                            <div>
                                                <span className="font-medium text-gray-700 dark:text-gray-300">Leadrs Rate:</span>
                                                <span className="ml-2 text-gray-900 dark:text-white">{selectedLead.leadrsRate ? `${selectedLead.leadrsRate}/10` : '-'}</span>
                                            </div>
                                            <div>
                                                <span className="font-medium text-gray-700 dark:text-gray-300">Customer Rate:</span>
                                                <span className="ml-2 text-gray-900 dark:text-white">{selectedLead.customerRate >= 0 ? `${selectedLead.customerRate}/10` : '-'}</span>
                                            </div>
                                            <div>
                                                <span className="font-medium text-gray-700 dark:text-gray-300">Seller Price:</span>
                                                <span className="ml-2 text-gray-900 dark:text-white">{selectedLead.leadPrice ? `€${selectedLead.leadPrice.toFixed(2)}` : '-'}</span>
                                            </div>
                                            <div>
                                                <span className="font-medium text-gray-700 dark:text-gray-300">Buyer Price:</span>
                                                <span className="ml-2 text-gray-900 dark:text-white">{selectedLead.buyerPrice ? `€${selectedLead.buyerPrice.toFixed(2)}` : '-'}</span>
                                            </div>
                                            {selectedLead.buyerPrice && selectedLead.leadPrice && (
                                                <div className="col-span-2">
                                                    <span className="font-medium text-gray-700 dark:text-gray-300">Commission:</span>
                                                    <span className="ml-2 text-green-600 dark:text-green-400 font-semibold">€{(selectedLead.buyerPrice - selectedLead.leadPrice).toFixed(2)}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {selectedLead.comment && (
                                        <div>
                                            <h3 className="text-lg font-semibold mb-3 text-navy-700 dark:text-white">Comment</h3>
                                            <div className="text-sm text-gray-700 dark:text-gray-300">{selectedLead.comment}</div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="sticky bottom-0 border-t border-gray-200 dark:border-navy-600 bg-white dark:bg-navy-800 px-6 py-4">
                            <button
                                onClick={closeModal}
                                className="rounded-lg bg-blue-500 px-4 py-2 text-white hover:bg-blue-600 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
